import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Truck,
  MapPin,
  Clock,
  CheckCircle2,
  AlertCircle,
  ChevronRight,
  Package,
  Navigation,
  Wallet,
  Calendar,
  RefreshCw,
  Car
} from 'lucide-react';
import { formatMerchandiseType } from '../../utils/formatters';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DriverDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [currentMission, setCurrentMission] = useState(null);
  const [pendingMissions, setPendingMissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [availability, setAvailability] = useState(user?.availability || 'disponible');

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      const headers = { Authorization: 'Bearer cookie-auth' };

      // Fetch stats
      const statsRes = await axios.get(`${API}/driver/stats`, { headers });
      setStats(statsRes.data);
      setAvailability(statsRes.data.availability || 'disponible');

      // Fetch missions
      const [activeRes, pendingRes] = await Promise.all([
        axios.get(`${API}/driver/missions?status=active&limit=1`, { headers }),
        axios.get(`${API}/driver/missions?status=pending&limit=5`, { headers })
      ]);

      if (activeRes.data.missions?.length > 0) {
        setCurrentMission(activeRes.data.missions[0]);
      }
      setPendingMissions(pendingRes.data.missions || []);

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const handleAvailabilityChange = async (newStatus) => {
    try {
      await axios.put(
        `${API}/driver/availability`,
        { status: newStatus },
        {}
      );
      setAvailability(newStatus);
    } catch (error) {
      console.error('Error updating availability:', error);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      planifiee: { label: 'Planifiée', class: 'bg-blue-100 text-blue-800' },
      prete_au_chargement: { label: 'Au chargement', class: 'bg-teal-100 text-teal-800' },
      en_cours_de_chargement: { label: 'Chargement', class: 'bg-orange-100 text-orange-800' },
      charge_en_route: { label: 'En route', class: 'bg-emerald-100 text-emerald-800' },
      livree: { label: 'Livrée', class: 'bg-green-100 text-green-800' }
    };
    const config = statusConfig[status] || { label: status, class: 'bg-gray-100 text-gray-800' };
    return <Badge className={config.class}>{config.label}</Badge>;
  };

  const getAvailabilityColor = (status) => {
    switch (status) {
      case 'disponible': return 'bg-green-500';
      case 'occupe': return 'bg-orange-500';
      case 'inactif': return 'bg-gray-500';
      default: return 'bg-gray-500';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR').format(amount || 0) + ' FCFA';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-[#1db584]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-lg md:text-2xl font-bold text-gray-900">
            Bonjour, {user?.firstName}
          </h1>
          <p className="text-gray-600">
            {user?.accountType === 'company' ? user?.companyName : 'Chauffeur indépendant'}
          </p>
        </div>

        {/* Availability Selector */}
        <div className="flex items-center gap-3">
          <div className={`w-3 h-3 rounded-full ${getAvailabilityColor(availability)}`} />
          <Select value={availability} onValueChange={handleAvailabilityChange}>
            <SelectTrigger className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="disponible">
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-green-500" />
                  Disponible
                </span>
              </SelectItem>
              <SelectItem value="occupe">
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-orange-500" />
                  Occupé
                </span>
              </SelectItem>
              <SelectItem value="inactif">
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-gray-500" />
                  Inactif
                </span>
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <Clock className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats?.pending || 0}</p>
              <p className="text-sm text-gray-500">En attente</p>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
              <Navigation className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats?.active || 0}</p>
              <p className="text-sm text-gray-500">En cours</p>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats?.thisMonthCompleted || 0}</p>
              <p className="text-sm text-gray-500">Ce mois</p>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
              <Wallet className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-lg font-bold text-gray-900">{formatCurrency(stats?.totalEarnings)}</p>
              <p className="text-sm text-gray-500">Gains totaux</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Current Mission */}
      {currentMission && (
        <Card className="overflow-hidden border-l-4 border-l-[#1db584]">
          <div className="p-4 bg-[#1db584]/5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#1db584] flex items-center justify-center">
                  <Truck className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Mission en cours</h3>
                  <p className="text-sm text-gray-600">{currentMission.operationNumber}</p>
                </div>
              </div>
              {getStatusBadge(currentMission.status)}
            </div>
          </div>
          <div className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <MapPin className="w-4 h-4 text-green-500" />
              <span className="text-sm">{currentMission.departure}</span>
              <ChevronRight className="w-4 h-4 text-gray-400" />
              <MapPin className="w-4 h-4 text-red-500" />
              <span className="text-sm">{currentMission.arrival}</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4 text-sm text-gray-500">
                <span className="flex items-center gap-1">
                  <Package className="w-4 h-4" />
                  {formatMerchandiseType(currentMission.merchandiseType)} • {currentMission.weight}T
                </span>
                <span className="flex items-center gap-1">
                  <Car className="w-4 h-4" />
                  {currentMission.vehiclePlate}
                </span>
              </div>
              <Link to={`/chauffeur/missions/${currentMission.id}`}>
                <Button size="sm" className="bg-[#1db584] hover:bg-[#16a075]">
                  Gérer la mission
                </Button>
              </Link>
            </div>
          </div>
        </Card>
      )}

      {/* Pending Missions */}
      <Card>
        <div className="p-4 border-b flex items-center justify-between">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
            <Clock className="w-5 h-5 text-blue-500" />
            Missions en attente ({pendingMissions.length})
          </h3>
          <Link to="/chauffeur/missions">
            <Button variant="ghost" size="sm">Voir tout</Button>
          </Link>
        </div>
        <div className="divide-y">
          {pendingMissions.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <AlertCircle className="w-12 h-12 mx-auto mb-2 text-gray-300" />
              <p>Aucune mission en attente</p>
            </div>
          ) : (
            pendingMissions.map((mission) => (
              <Link
                key={mission.id}
                to={`/chauffeur/missions/${mission.id}`}
                className="p-4 hover:bg-gray-50 flex items-center justify-between"
              >
                <div>
                  <p className="font-medium text-gray-900">{mission.operationNumber}</p>
                  <p className="text-sm text-gray-500">
                    {mission.departure} → {mission.arrival}
                  </p>
                  <p className="text-sm text-gray-500">
                    {formatMerchandiseType(mission.merchandiseType)} • {mission.weight}T
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-[#1db584]">
                    {formatCurrency(mission.driverPayment)}
                  </p>
                  {mission.scheduledLoadingDate && (
                    <p className="text-xs text-gray-500 flex items-center gap-1 justify-end">
                      <Calendar className="w-3 h-3" />
                      {new Date(mission.scheduledLoadingDate).toLocaleDateString('fr-FR')}
                    </p>
                  )}
                </div>
              </Link>
            ))
          )}
        </div>
      </Card>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 gap-4">
        <Link to="/chauffeur/vehicules">
          <Card className="p-4 hover:shadow-md transition-shadow cursor-pointer">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Car className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="font-medium text-gray-900">Ma Flotte</p>
                <p className="text-sm text-gray-500">Gérer mes véhicules</p>
              </div>
            </div>
          </Card>
        </Link>
        <Link to="/chauffeur/profil">
          <Card className="p-4 hover:shadow-md transition-shadow cursor-pointer">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="font-medium text-gray-900">Mon Profil</p>
                <p className="text-sm text-gray-500">Documents & infos</p>
              </div>
            </div>
          </Card>
        </Link>
      </div>
    </div>
  );
};

export default DriverDashboard;
